import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Icon, IconName } from "./Icon";

interface ChatStatsProps {
  authToken: string;
}

interface HourlyStats {
  hour: number;
  totalMessages: number;
  uniqueChannels: number;
  uniqueUsers: number;
  halMessages: number;
}

interface DailyStats {
  date: string;
  totalMessages: number;
  uniqueChannels: number;
  uniqueUsers: number;
  halMessages: number;
}

interface TopChannel {
  channel: string;
  messageCount: number;
  uniqueUsers: number;
  halMessages: number;
}

interface ChatStatsData {
  hourly: HourlyStats[];
  daily: DailyStats[];
}

type TimeFilter = "24h" | "30d";

const ChatStats: React.FC<ChatStatsProps> = ({ authToken }) => {
  const [timeFilter, setTimeFilter] = useState<TimeFilter>("24h");
  const [statsData, setStatsData] = useState<ChatStatsData | null>(null);
  const [topChannels, setTopChannels] = useState<TopChannel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchChatStats = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/admin/chat-stats`, {
        params: { timeFilter },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setStatsData(response.data);
      setError(null);
    } catch (err) {
      console.error("Error fetching chat stats:", err);
      setError("Failed to load chat statistics");
    } finally {
      setLoading(false);
    }
  }, [timeFilter, authToken]);

  const fetchTopChannels = useCallback(async () => {
    try {
      const response = await axios.get(`/api/admin/top-active-channels`, {
        params: { timeFilter },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setTopChannels(response.data);
    } catch (err) {
      console.error("Error fetching top channels:", err);
    }
  }, [timeFilter, authToken]);

  useEffect(() => {
    fetchChatStats();
    fetchTopChannels();
  }, [fetchChatStats, fetchTopChannels]);

  const renderChart = (
    data: HourlyStats[] | DailyStats[],
    metric: keyof HourlyStats & keyof DailyStats,
    color: string,
    title: string,
    icon: IconName,
  ) => {
    if (!data || data.length === 0) return null;

    const maxValue = Math.max(...data.map((d) => d[metric] as number));
    const chartHeight = 200;

    const formatLabel = (item: HourlyStats | DailyStats) => {
      if ("hour" in item) {
        return `${item.hour.toString().padStart(2, "0")}:00`;
      } else {
        const date = new Date(item.date);
        return `${date.getMonth() + 1}/${date.getDate()}`;
      }
    };

    return (
      <div
        style={{
          backgroundColor: "#2d3748",
          borderRadius: "12px",
          padding: "24px",
          border: "1px solid #4a5568",
        }}
      >
        <h4
          style={{
            fontSize: "18px",
            fontWeight: "600",
            color: "#e2e8f0",
            marginBottom: "16px",
          }}
        >
          <Icon
            name={icon}
            size={18}
            style={{ verticalAlign: "-3px", marginRight: "8px" }}
          />
          {title}
        </h4>
        <div
          style={{
            position: "relative",
            height: `${chartHeight}px`,
            marginBottom: "40px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "flex-end",
              height: "100%",
              gap: "2px",
            }}
          >
            {data.map((item, idx) => {
              const value = item[metric] as number;
              const height = maxValue > 0 ? (value / maxValue) * 100 : 0;
              return (
                <div
                  key={idx}
                  style={{
                    flex: 1,
                    backgroundColor: color,
                    height: `${height}%`,
                    borderRadius: "2px 2px 0 0",
                    opacity: 0.8,
                    transition: "opacity 0.2s",
                    cursor: "pointer",
                    minHeight: value > 0 ? "2px" : "0",
                  }}
                  title={`${formatLabel(item)}: ${value}`}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.8")}
                />
              );
            })}
          </div>
          <div
            style={{
              position: "absolute",
              bottom: "-30px",
              left: 0,
              right: 0,
              borderTop: "1px solid #4a5568",
              paddingTop: "8px",
              display: "flex",
              justifyContent: "space-between",
              fontSize: "12px",
              color: "#cbd5e0",
            }}
          >
            <span>{formatLabel(data[0])}</span>
            <span>{formatLabel(data[data.length - 1])}</span>
          </div>
          <div
            style={{
              position: "absolute",
              top: "-32px",
              right: 0,
              fontSize: "24px",
              fontWeight: "bold",
              color,
            }}
          >
            {Math.max(
              ...data.map((item) => item[metric] as number),
            ).toLocaleString()}
            <span
              style={{
                fontSize: "12px",
                fontWeight: "normal",
                marginLeft: "8px",
              }}
            >
              (max)
            </span>
          </div>
        </div>
      </div>
    );
  };

  if (loading && !statsData) {
    return (
      <div style={{ color: "#a0aec0", padding: "20px" }}>
        Loading chat statistics...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ color: "#F56565", padding: "20px" }}>Error: {error}</div>
    );
  }

  const currentData =
    timeFilter === "24h" ? statsData?.hourly : statsData?.daily;

  return (
    <div>
      <h3
        style={{
          fontSize: "20px",
          fontWeight: "600",
          color: "#e2e8f0",
          marginBottom: "24px",
        }}
      >
        Global Chat Statistics
      </h3>

      {/* Time Filter */}
      <div
        style={{
          display: "flex",
          gap: "12px",
          marginBottom: "24px",
          backgroundColor: "#2d3748",
          padding: "8px",
          borderRadius: "8px",
          width: "fit-content",
        }}
      >
        <button
          onClick={() => setTimeFilter("24h")}
          style={{
            padding: "8px 16px",
            borderRadius: "6px",
            border: "none",
            backgroundColor: timeFilter === "24h" ? "#9146FF" : "transparent",
            color: timeFilter === "24h" ? "#fff" : "#cbd5e0",
            cursor: "pointer",
            fontWeight: "500",
            transition: "all 0.2s",
          }}
        >
          Last 24 Hours
        </button>
        <button
          onClick={() => setTimeFilter("30d")}
          style={{
            padding: "8px 16px",
            borderRadius: "6px",
            border: "none",
            backgroundColor: timeFilter === "30d" ? "#9146FF" : "transparent",
            color: timeFilter === "30d" ? "#fff" : "#cbd5e0",
            cursor: "pointer",
            fontWeight: "500",
            transition: "all 0.2s",
          }}
        >
          Last 30 Days
        </button>
      </div>

      {/* Charts Grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(500px, 1fr))",
          gap: "24px",
        }}
      >
        {currentData && (
          <>
            {renderChart(
              currentData,
              "totalMessages",
              "#9146FF",
              "Total Messages",
              "bar-chart-3",
            )}
            {renderChart(
              currentData,
              "uniqueChannels",
              "#10B981",
              "Active Channels",
              "tv",
            )}
            {renderChart(
              currentData,
              "uniqueUsers",
              "#F59E0B",
              "Active Users",
              "users-round",
            )}
            {renderChart(
              currentData,
              "halMessages",
              "#EF4444",
              "HAL Messages",
              "bot",
            )}
          </>
        )}
      </div>

      {/* Top 10 Most Active Channels */}
      {topChannels.length > 0 && (
        <div
          style={{
            marginTop: "32px",
            backgroundColor: "#2d3748",
            borderRadius: "12px",
            padding: "24px",
            border: "1px solid #4a5568",
          }}
        >
          <h4
            style={{
              fontSize: "18px",
              fontWeight: "600",
              color: "#e2e8f0",
              marginBottom: "16px",
            }}
          >
            <Icon
              name="trophy"
              size={18}
              style={{ verticalAlign: "-3px", marginRight: "8px" }}
            />
            Top 10 Most Active Channels
          </h4>
          <div
            style={{
              display: "grid",
              gap: "12px",
            }}
          >
            {topChannels.map((channel, index) => (
              <div
                key={channel.channel}
                style={{
                  display: "grid",
                  gridTemplateColumns: "40px 1fr auto auto auto",
                  gap: "16px",
                  alignItems: "center",
                  padding: "12px",
                  backgroundColor: "#1a202c",
                  borderRadius: "8px",
                  border: "1px solid #4a5568",
                }}
              >
                <div
                  style={{
                    fontSize: "18px",
                    fontWeight: "bold",
                    color: index < 3 ? "#F59E0B" : "#CBD5E0",
                  }}
                >
                  #{index + 1}
                </div>
                <div style={{ color: "#E2E8F0", fontWeight: "500" }}>
                  {channel.channel}
                </div>
                <div style={{ color: "#9146FF", textAlign: "right" }}>
                  {channel.messageCount.toLocaleString()} msgs
                </div>
                <div style={{ color: "#F59E0B", textAlign: "right" }}>
                  {channel.uniqueUsers} users
                </div>
                <div style={{ color: "#EF4444", textAlign: "right" }}>
                  {channel.halMessages} HAL
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Summary Stats */}
      {currentData && currentData.length > 0 && (
        <div
          style={{
            marginTop: "32px",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "16px",
          }}
        >
          <div
            style={{
              backgroundColor: "#2d3748",
              borderRadius: "8px",
              padding: "16px",
              border: "1px solid #4a5568",
            }}
          >
            <div style={{ color: "#a0aec0", fontSize: "14px" }}>
              Average Messages per {timeFilter === "24h" ? "Hour" : "Day"}
            </div>
            <div
              style={{ color: "#9146FF", fontSize: "24px", fontWeight: "bold" }}
            >
              {Math.round(
                (currentData as any[]).reduce(
                  (sum: number, item: any) => sum + item.totalMessages,
                  0,
                ) / currentData.length,
              ).toLocaleString()}
            </div>
          </div>
          <div
            style={{
              backgroundColor: "#2d3748",
              borderRadius: "8px",
              padding: "16px",
              border: "1px solid #4a5568",
            }}
          >
            <div style={{ color: "#a0aec0", fontSize: "14px" }}>
              HAL Response Rate
            </div>
            <div
              style={{ color: "#EF4444", fontSize: "24px", fontWeight: "bold" }}
            >
              {(currentData as any[]).reduce(
                (sum: number, item: any) => sum + item.totalMessages,
                0,
              ) > 0
                ? Math.round(
                    ((currentData as any[]).reduce(
                      (sum: number, item: any) => sum + item.halMessages,
                      0,
                    ) /
                      (currentData as any[]).reduce(
                        (sum: number, item: any) => sum + item.totalMessages,
                        0,
                      )) *
                      100,
                  )
                : 0}
              %
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(ChatStats);
